import React, { useCallback, useState } from 'react';
import { Linking, Platform, ScrollView } from 'react-native';
import * as Notifications from 'expo-notifications';
import { useIsFocused } from 'expo-router/react-navigation';
import { useTranslation } from 'react-i18next';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { Text } from '@/components/gluestack/text';
import { VStack } from '~/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Card } from '~/components/gluestack/card';
import { Divider } from '@/components/gluestack/divider';
import { SettingsToggle } from '@/components/ui/SettingsToggle';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { useAccountSettings, useUpdateAccountSetting } from '@/hooks/useAccountSettings';
import { useAppState } from '@/hooks/useAppState';
import { usePermissionsTick } from '@/components/providers/PermissionsBootstrap';
import type { AccountSettingsResponse } from '@/services/domain/AccountTypes';
import {
  Mail01Icon,
  UserAdd01Icon,
  Image02Icon,
  FileEditIcon,
  Message01Icon,
  NotificationOff02Icon,
} from '@hugeicons-pro/core-stroke-rounded';

export default function NotificationsSettings() {
  const { t } = useTranslation();
  const isScreenActive = useIsFocused();
  const permissionsTick = usePermissionsTick();
  const { data: accountSettings, isPending: isLoading } = useAccountSettings();
  const { updateSetting } = useUpdateAccountSetting();

  // null = unknown (initial), true/false = resolved. We only render the banner
  // for an explicit `false` so it doesn't flash on the first frame.
  const [hasNotificationPermission, setHasNotificationPermission] = useState<boolean | null>(null);

  const recheckPermission = useCallback(async () => {
    if (Platform.OS === 'web') return;
    try {
      const { status } = await Notifications.getPermissionsAsync();
      setHasNotificationPermission(status === 'granted');
    } catch {
      setHasNotificationPermission(null);
    }
  }, []);

  React.useEffect(() => {
    if (!isScreenActive) return;
    recheckPermission();
  }, [isScreenActive, permissionsTick, recheckPermission]);

  useAppState({ onForeground: recheckPermission });

  const handleSettingChange = (key: keyof AccountSettingsResponse, newValue: boolean) => {
    if (!accountSettings) return;
    updateSetting(key, newValue);
  };

  const showPermissionBanner = Platform.OS !== 'web' && hasNotificationPermission === false;

  return (
    <>
      <ScreenHeader
        options={{
          title:
            Platform.OS === 'web'
              ? t('settings.notifications.title')
              : t('notifications.titleNative'),
        }}
      />

      <ScrollView>
        <VStack className="mx-auto w-full max-w-4xl p-3 md:pt-0" space="md">
          {showPermissionBanner ? (
            <Card size="md" className="border border-warning-200 bg-warning-50">
              <HStack className="items-center gap-3">
                <HugeIconsWrapper
                  icon={NotificationOff02Icon}
                  size={24}
                  className="shrink-0 color-warning-600"
                />
                <VStack className="flex-1 gap-0.5">
                  <Text size="sm" className="font-semibold text-warning-700">
                    {t('notifications.permissionDisabledTitle')}
                  </Text>
                  <Text size="xs" className="text-typography-600">
                    {t('notifications.permissionDisabledDescription')}
                  </Text>
                </VStack>
                <ButtonWrapper
                  size="sm"
                  variant="outline"
                  action="primary"
                  text={t('notifications.openSettings')}
                  onPress={() => Linking.openSettings()}
                />
              </HStack>
            </Card>
          ) : null}
          <Text size="sm" className="mx-4 mt-4 text-typography-600">
            {t('settings.notifications.description')}
          </Text>

          <Card className="gap-3" size="md">
            {isLoading ? (
              <Text className="text-typography-500">{t('common.loading')}</Text>
            ) : accountSettings ? (
              <>
                <SettingsToggle
                  icon={Mail01Icon}
                  label={t('settings.notifications.newMessage')}
                  description={t('settings.notifications.newMessageDescription')}
                  value={accountSettings.NotifyOnNewMessage}
                  onValueChange={(value) => handleSettingChange('NotifyOnNewMessage', value)}
                />
                <Divider className="-my-2" />
                <SettingsToggle
                  icon={UserAdd01Icon}
                  label={t('settings.notifications.newFollower')}
                  description={t('settings.notifications.newFollowerDescription')}
                  value={accountSettings.NotifyOnNewFollower}
                  onValueChange={(value) => handleSettingChange('NotifyOnNewFollower', value)}
                />
              </>
            ) : null}
          </Card>

          <Text size="sm" className="mx-4 mt-4 text-typography-600">
            {t('settings.notifications.futureNotifications')}
          </Text>

          <Card className="gap-3" size="md">
            {isLoading ? (
              <Text className="text-typography-500">{t('common.loading')}</Text>
            ) : accountSettings ? (
              <>
                <SettingsToggle
                  icon={Image02Icon}
                  label={t('settings.notifications.newPhotoComment')}
                  description={t('settings.notifications.newPhotoCommentDescription')}
                  value={accountSettings.NotifyOnNewPhotoComment}
                  onValueChange={(value) => handleSettingChange('NotifyOnNewPhotoComment', value)}
                />
                <Divider className="-my-2" />
                <SettingsToggle
                  icon={FileEditIcon}
                  label={t('settings.notifications.newPostComment')}
                  description={t('settings.notifications.newPostCommentDescription')}
                  value={accountSettings.NotifyOnNewPostComment}
                  onValueChange={(value) => handleSettingChange('NotifyOnNewPostComment', value)}
                />
                <Divider className="-my-2" />
                <SettingsToggle
                  icon={Message01Icon}
                  label={t('settings.notifications.newWallPost')}
                  description={t('settings.notifications.newWallPostDescription')}
                  value={accountSettings.NotifyOnNewWallPost}
                  onValueChange={(value) => handleSettingChange('NotifyOnNewWallPost', value)}
                />
              </>
            ) : null}
          </Card>
        </VStack>
      </ScrollView>
    </>
  );
}
